import React, { RefObject, useEffect, useRef, useState } from 'react';
import { asObject, prop } from '../../../../common/utils/immutableHelper';
import { scale } from '../../../../common/maths/geometry/point';
import { isNull } from 'lodash/fp';

// Utils
import { isChangeTypeofRectSize } from '../resizeUtils';
import { getDomElementId } from '../../utils/elementUtil';
import {
    getMedia,
    getMediaHeight,
    getMediaWidth,
    getWidth,
} from '../../../../common/elements/utils/elementPropertyUtils';
import useNonMediaContentResize from './useNonMediaContentResize';

// Types
import {
    ResizeAspectLock,
    ResizeChanges,
    SetElementSizeAndPosition,
    SetTempElementSize,
    MediaElementContentVisibility,
} from '../resizeTypes';
import { Point } from '../../../../common/maths/geometry/pointTypes';
import { RectSize, RectSizeHeightOptional } from '../../../../common/maths/geometry/rect/rectTypes';
import { ImMNElement, MNMediaContent } from '../../../../common/elements/elementModelTypes';
import { BORDER_OFFSET_PX } from '../../../utils/grid/gridUtils';
import { DispatchUpdateElement } from '../../actions/elementActionTypes';

export type RequiredMediaResizeProps = {
    isResizing: boolean;
    elementId: string;
    element: ImMNElement;
    gridSize: number;
    setElementSizeAndPosition: SetElementSizeAndPosition;
    getContextZoomScale: () => number;
    tempSize: RectSize | null;
    setTempElementSize: SetTempElementSize;
    resizeAspectLock: ResizeAspectLock;
    dispatchUpdateElement: DispatchUpdateElement;
    minWidth: number;
    minHeight: number;
};

export type ElementSaveMediaResize = {
    tempMediaSize: RectSize | null;
    setTempElementSize: SetTempElementSize;
    setMediaSize: (size: RectSize) => void;
    mediaElementRef: RefObject<HTMLDivElement>;
    handleDoubleClick: (event: React.MouseEvent) => void;
    setElementSizeAndPosition: SetElementSizeAndPosition;
    tempMediaElementVisibility: MediaElementContentVisibility;
    getNonMediaHeight: () => number;
};

const useElementSaveMediaResize = <T extends RequiredMediaResizeProps>(
    props: T,
    getDefaultWidth: (props: T) => number,
    getDefaultMaxWidth?: (props: T) => number,
): ElementSaveMediaResize => {
    const {
        isResizing,
        elementId,
        element,
        gridSize,
        getContextZoomScale,
        tempSize,
        resizeAspectLock,
        dispatchUpdateElement,
        minWidth,
        minHeight,
    } = props;

    const zoomScale = getContextZoomScale();

    const nonMediaHeightPxRef = useRef<number | null>(null);
    const mediaElementRef = useRef<HTMLDivElement | null>(null);
    const latestMediaSizeRef = useRef<RectSize | null>(null);
    const elementDomNodeRef = useRef<HTMLElement | null>(null);
    const [tempMediaSize, setTempMediaSize] = useState<RectSize | null>(null);

    const defaultWidth = getDefaultWidth(props);

    const shouldUpdateMediaSize = resizeAspectLock !== ResizeAspectLock.MEDIA;

    const resetState = () => {
        setTempMediaSize(null);
    };

    useEffect(() => {
        nonMediaHeightPxRef.current = null;
        elementDomNodeRef.current = null;
    }, [element]);

    useEffect(() => {
        if (!isResizing) return;

        if (!shouldUpdateMediaSize) {
            resetState();
        } else {
            setTempMediaSize(latestMediaSizeRef.current);
        }
    }, [shouldUpdateMediaSize]);

    const getMediaSize = (elementSizeGridUnits: RectSize) => {
        // @ts-ignore scale function works correctly with Size type
        const desiredElementSizePx: RectSize = scale(gridSize, elementSizeGridUnits);
        const desiredWidthPx = desiredElementSizePx.width;

        const desiredHeightPx = desiredElementSizePx.height - (nonMediaHeightPxRef.current || 0);

        const minHeightPx = gridSize * minHeight;
        const minWidthPx = gridSize * minWidth;

        return {
            width: Math.max(minWidthPx, desiredWidthPx),
            height: Math.max(minHeightPx, desiredHeightPx),
        };
    };

    const getNonMediaHeight = () => {
        if (!mediaElementRef.current) return 0;

        const mediaBoundingRect = mediaElementRef.current?.getBoundingClientRect();

        // We need the exact measurements at this point in time so we unfortunately can't use the measurements
        // store.  The measurements store can potentially lag in the update, which causes an incorrect
        // nonMediaHeight from being calculated.
        if (!elementDomNodeRef.current) {
            elementDomNodeRef.current = document.getElementById(getDomElementId(elementId));
        }
        const elementSize = elementDomNodeRef.current?.getBoundingClientRect();

        const nonMediaHeightActualPx = prop('height', elementSize) - (mediaBoundingRect?.height || 0);

        return nonMediaHeightActualPx / zoomScale;
    };

    const ensureNonMediaHeightIsSet = () => {
        if (!isNull(nonMediaHeightPxRef.current)) return;

        nonMediaHeightPxRef.current = getNonMediaHeight();
    };

    const setTempElementSize = (elementSizeGridUnits: RectSizeHeightOptional | null) => {
        const { setTempElementSize } = props;

        if (!elementSizeGridUnits) {
            resetState();
            setTempElementSize(null);
            return;
        }

        if (!isChangeTypeofRectSize(elementSizeGridUnits)) return;

        ensureNonMediaHeightIsSet();

        const newMediaSize = getMediaSize(elementSizeGridUnits);

        // I don't know why this is necessary - but without it the temp width is 2px larger than the saved width
        // which results in a jump on resize start due to the aspect ratio changing (see ResponsiveProgressiveImage).
        newMediaSize.width = newMediaSize.width - BORDER_OFFSET_PX;

        latestMediaSizeRef.current = newMediaSize;
        if (shouldUpdateMediaSize) setTempMediaSize(newMediaSize);

        setTempElementSize(elementSizeGridUnits);
    };

    const tempMediaElementVisibility = useNonMediaContentResize({
        tempSize,
        element,
        resizeAspectLock,
        nonMediaHeightPxRef,
        getNonMediaHeight,
        minHeight,
        gridSize,
        setTempElementSize,
    });

    const updateSize = (sizeGridUnits: RectSize, mediaSizePx: RectSize, position?: Point) => {
        const targetWidthGridUnits = Math.max(Math.round(sizeGridUnits.width), minWidth);
        const showMediaOnly = tempMediaElementVisibility === MediaElementContentVisibility.SHOW_MEDIA_ONLY;

        if (!shouldUpdateMediaSize) {
            const changes = {
                width: targetWidthGridUnits,
                showMediaOnly,
            };
            props.setElementSizeAndPosition(changes, position);
            return;
        }

        let initialMediaState = getMedia(element);
        initialMediaState = initialMediaState && initialMediaState.toJS();

        let targetWidthPx = Math.max(mediaSizePx.width, minWidth);
        let targetHeightPx = Math.max(mediaSizePx.height, minHeight);

        const heightGridUnits = (targetWidthGridUnits / targetWidthPx) * targetHeightPx;

        // When snapping the height the px is the number of pixels rounded to a grid point minus two for borders
        targetHeightPx = Math.round(heightGridUnits) * gridSize - 2;
        // The target width must also be adjusted because the height is set using padding which is proportional
        // to the width
        targetWidthPx = Math.round(targetWidthPx / gridSize) * gridSize - 2;

        const changes = {
            width: targetWidthGridUnits,
            media: {
                ...initialMediaState,
                width: targetWidthPx,
                height: targetHeightPx,
            },
            showMediaOnly,
        };
        props.setElementSizeAndPosition(changes, position);
    };

    const setElementSizeAndPosition = (elementSizeGridUnits: ResizeChanges, position?: Point) => {
        // Although different elements can add different change values, we know within media resizing we only want to work with width and height values
        if (!isChangeTypeofRectSize(elementSizeGridUnits)) {
            if (position) {
                props.setElementSizeAndPosition(null, position);
            }
            return;
        }

        ensureNonMediaHeightIsSet();

        const mediaSize = getMediaSize(elementSizeGridUnits);
        updateSize(elementSizeGridUnits, mediaSize, position);
    };

    const setMediaSize = (mediaSizePx: RectSize) => {
        ensureNonMediaHeightIsSet();

        if (!nonMediaHeightPxRef.current) return;

        const nonMediaHeightGU = nonMediaHeightPxRef.current / gridSize;

        const elementSize = {
            width: mediaSizePx.width / gridSize,
            height: mediaSizePx.height / gridSize + nonMediaHeightGU,
        };
        updateSize(elementSize, mediaSizePx);
    };

    const handleDoubleClick = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const hasMediaDimensions = getMediaWidth(element) && getMediaHeight(element);
        if (hasMediaDimensions) {
            // Remove the forced aspect ratio
            const media = asObject<MNMediaContent>(getMedia(element));
            delete media.width;
            delete media.height;

            return dispatchUpdateElement({
                id: elementId,
                changes: { media, showMediaOnly: false },
            });
        }

        if (!getDefaultMaxWidth || !defaultWidth) return;

        const widthIsDefault = getWidth(element) === defaultWidth;
        const width = widthIsDefault ? getDefaultMaxWidth(props) / gridSize : defaultWidth;

        dispatchUpdateElement({
            id: elementId,
            changes: {
                width,
            },
        });
    };

    return {
        tempMediaSize,
        setTempElementSize,
        setMediaSize,
        mediaElementRef,
        handleDoubleClick,
        setElementSizeAndPosition,
        tempMediaElementVisibility,
        getNonMediaHeight,
    };
};

export default useElementSaveMediaResize;
